import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Cohort, Level, Profile, CohortFormData } from '@/types'
import {
  ArrowLeft,
  Save,
  Users,
  Calendar,
  User,
  Layers,
  Edit,
  Plus,
  X,
  Search
} from 'lucide-react'

interface StudentProfile extends Profile {
  selected?: boolean
}

export default function CohortForm() {
  const { cohortId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isEditing = Boolean(cohortId) && cohortId !== 'new'
  const isViewMode = isEditing && !location.pathname.endsWith('/edit')

  const [levels, setLevels] = useState<Level[]>([])
  const [teachers, setTeachers] = useState<Profile[]>([])
  const [students, setStudents] = useState<StudentProfile[]>([])
  const [studentSearch, setStudentSearch] = useState('')
  const [showStudentSelector, setShowStudentSelector] = useState(false)

  const [formData, setFormData] = useState<CohortFormData>({
    name: '',
    description: '',
    level_id: '',
    teacher_id: '',
    substitute_teacher_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    schedule_day: new Date().toLocaleDateString('es-ES', { weekday: 'long' }).replace(/^\w/, c => c.toUpperCase()),
    schedule_start_time: '',
    schedule_end_time: '',
    max_students: 12,
    student_ids: [],
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadData()
    if (isEditing) {
      loadCohort()
    }
  }, [cohortId])

  const loadData = async () => {
    try {
      const [levelsRes, teachersRes, studentsRes] = await Promise.all([
        supabase.from('levels').select('*').eq('is_active', true).order('order_num'),
        supabase.from('profiles').select('*').eq('role', 'teacher'),
        supabase.from('profiles').select('*').eq('role', 'student')
      ])

      if (levelsRes.error) throw levelsRes.error
      if (teachersRes.error) throw teachersRes.error
      if (studentsRes.error) throw studentsRes.error

      setLevels((levelsRes.data as unknown as Level[]) || [])
      setTeachers((teachersRes.data as unknown as Profile[]) || [])
      setStudents((studentsRes.data as unknown as StudentProfile[]) || [])
    } catch (error) {
      console.error('Error loading data:', error)
    }
  }

  const loadCohort = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('cohorts')
        .select('*')
        .eq('id', cohortId as string)
        .single()

      if (error) throw error

      const cohortData = data as unknown as Cohort

      setFormData({
        name: cohortData.name,
        description: cohortData.description || '',
        level_id: cohortData.level_id,
        teacher_id: cohortData.teacher_id,
        substitute_teacher_id: cohortData.substitute_teacher_id || '',
        start_date: cohortData.start_date,
        end_date: cohortData.end_date || '',
        schedule_day: cohortData.schedule_day || (cohortData.start_date
          ? new Date(cohortData.start_date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long' }).replace(/^\w/, c => c.toUpperCase())
          : ''),
        schedule_start_time: cohortData.schedule_start_time || '',
        schedule_end_time: cohortData.schedule_end_time || '',
        max_students: cohortData.max_students,
        student_ids: cohortData.student_ids || [],
        is_active: cohortData.is_active
      })
    } catch (error) {
      console.error('Error loading cohort:', error)
      setError('Error al cargar la cohorte')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSaving(true)

    try {
      const cohortPayload = {
        name: formData.name,
        description: formData.description || null,
        level_id: formData.level_id,
        teacher_id: formData.teacher_id,
        substitute_teacher_id: formData.substitute_teacher_id || null,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        schedule_day: formData.schedule_day || null,
        schedule_start_time: formData.schedule_start_time || null,
        schedule_end_time: formData.schedule_end_time || null,
        max_students: formData.max_students,
        student_ids: formData.student_ids,
        is_active: formData.is_active
      }

      if (isEditing) {
        const { error } = await (supabase
          .from('cohorts') as any)
          .update({
            ...cohortPayload,
            updated_at: new Date().toISOString()
          })
          .eq('id', cohortId as string)

        if (error) throw error
      } else {
        const { error } = await (supabase
          .from('cohorts') as any)
          .insert(cohortPayload)

        if (error) throw error
      }

      navigate('/admin/cohorts')
    } catch (error: any) {
      console.error('Error saving cohort:', error)
      setError(error.message || 'Error al guardar la cohorte')
    } finally {
      setIsSaving(false)
    }
  }

  const addStudent = (studentId: string) => {
    if (formData.student_ids.length >= formData.max_students) {
      alert(`Máximo ${formData.max_students} estudiantes permitidos`)
      return
    }
    if (!formData.student_ids.includes(studentId)) {
      setFormData(prev => ({
        ...prev,
        student_ids: [...prev.student_ids, studentId]
      }))
    }
    setShowStudentSelector(false)
    setStudentSearch('')
  }

  const removeStudent = (studentId: string) => {
    setFormData(prev => ({
      ...prev,
      student_ids: prev.student_ids.filter(id => id !== studentId)
    }))
  }

  const getStudentName = (studentId: string) => {
    const student = students.find(s => s.id === studentId)
    return student ? `${student.first_name} ${student.last_name}` : 'Desconocido'
  }

  const filteredStudents = students.filter(s =>
    !formData.student_ids.includes(s.id) &&
    (`${s.first_name} ${s.last_name}`.toLowerCase().includes(studentSearch.toLowerCase()) ||
     s.email.toLowerCase().includes(studentSearch.toLowerCase()))
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/cohorts"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isViewMode ? 'Visualizar Cohorte' : (isEditing ? 'Editar Cohorte' : 'Nueva Cohorte')}
            </h1>
            <p className="text-gray-600">
              {isViewMode ? 'Vista previa de la cohorte' : (isEditing ? 'Modifica los datos de la cohorte' : 'Crea una nueva cohorte de estudiantes')}
            </p>
          </div>
        </div>
        {isViewMode && (
          <Link
            to={`/admin/cohorts/${cohortId}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Edit className="w-5 h-5" />
            Editar
          </Link>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Información de la Cohorte</h2>
          </div>

          <div>
            <label htmlFor="cohort-name" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de la Cohorte
            </label>
            <input id="cohort-name" type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ej: Cohorte Enero 2025"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
              disabled={isViewMode}
            />
          </div>

          <div>
            <label htmlFor="cohort-description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción (opcional)
            </label>
            <textarea id="cohort-description" value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe esta cohorte..."
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="w-4 h-4 text-[#0055A4] border-gray-300 rounded focus:ring-[#0055A4] disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Cohorte activa
            </label>
          </div>
        </div>

        {/* Level and Teacher */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-[#0055A4]/10">
              <Layers className="w-5 h-5 text-[#0055A4]" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Nivel y Profesor</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="cohort-level" className="block text-sm font-medium text-gray-700 mb-1">
                Nivel
              </label>
              <select id="cohort-level" value={formData.level_id}
                onChange={(e) => setFormData(prev => ({ ...prev, level_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              >
                <option value="">Seleccionar nivel...</option>
                {levels.map(level => (
                  <option key={level.id} value={level.id}>
                    Nivel {level.order_num}: {level.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="cohort-teacher" className="block text-sm font-medium text-gray-700 mb-1">
                Profesor principal
              </label>
              <select id="cohort-teacher" value={formData.teacher_id}
                onChange={(e) => setFormData(prev => ({ ...prev, teacher_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              >
                <option value="">Seleccionar profesor...</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.first_name} {teacher.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="cohort-substitute" className="block text-sm font-medium text-gray-700 mb-1">
                Profesor suplente{' '}
                <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <select id="cohort-substitute" value={formData.substitute_teacher_id}
                onChange={(e) => setFormData(prev => ({ ...prev, substitute_teacher_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              >
                <option value="">Sin suplente</option>
                {teachers
                  .filter(t => t.id !== formData.teacher_id)
                  .map(teacher => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name}
                    </option>
                  ))}
              </select>
              {formData.substitute_teacher_id && (
                <p className="text-xs text-[#0055A4] mt-1">
                  Al cambiar a suplente, todos los dominios de la cohorte pasan a ese profesor.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-100">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Fechas</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="cohort-start-date" className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Inicio
              </label>
              <input id="cohort-start-date" type="date"
                onChange={(e) => {
                  const date = e.target.value
                  const dayName = date
                    ? new Date(date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long' }).replace(/^\w/, c => c.toUpperCase())
                    : ''
                  setFormData(prev => ({ ...prev, start_date: date, schedule_day: dayName }))
                }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              />
            </div>
            <div>
              <label htmlFor="cohort-end-date" className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Fin (opcional)
              </label>
              <input id="cohort-end-date" type="date" value={formData.end_date} 
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
            <div>
              <label htmlFor="cohort-max-students" className="block text-sm font-medium text-gray-700 mb-1">
                Max. Estudiantes
              </label>
              <input id="cohort-max-students" type="number"
                min="1"
                max="50"
                value={formData.max_students}
                onChange={(e) => setFormData(prev => ({ ...prev, max_students: Number.parseInt(e.target.value) || 12 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-amber-100">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Horario</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="cohort-schedule-day" className="block text-sm font-medium text-gray-700 mb-1">
                Día de la semana
              </label>
              <input id="cohort-schedule-day" type="text" 
                value={formData.start_date
                  ? new Date(formData.start_date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long' }).replace(/^\w/, c => c.toUpperCase())
                  : 'Selecciona una fecha de inicio'}
                className="w-full px-3 py-2 border rounded-lg bg-gray-100 cursor-not-allowed"
                disabled
              />
            </div>
            <div>
              <label htmlFor="cohort-start-time" className="block text-sm font-medium text-gray-700 mb-1">
                Hora inicio
              </label>
              <input id="cohort-start-time" type="time"
                value={formData.schedule_start_time}
                onChange={(e) => setFormData(prev => ({ ...prev, schedule_start_time: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
            <div>
              <label htmlFor="cohort-end-time" className="block text-sm font-medium text-gray-700 mb-1">
                Hora fin
              </label>
              <input id="cohort-end-time" type="time" 
                value={formData.schedule_end_time}
                onChange={(e) => setFormData(prev => ({ ...prev, schedule_end_time: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
          </div>

          {formData.start_date && formData.schedule_start_time && formData.schedule_end_time && (
            <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg">
              Horario: <strong>{formData.schedule_day} de {formData.schedule_start_time} a {formData.schedule_end_time}</strong>
            </p>
          )}
        </div>

        {/* Students */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Estudiantes ({formData.student_ids.length}/{formData.max_students})
              </h2>
            </div>
            {!isViewMode && (
              <button
                type="button"
                onClick={() => setShowStudentSelector(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
                disabled={formData.student_ids.length >= formData.max_students}
              >
                <Plus className="w-4 h-4" />
                Agregar
              </button>
            )}
          </div>

          {/* Selected Students */}
          <div className="space-y-2">
            {formData.student_ids.length > 0 ? (
              formData.student_ids.map((studentId, index) => (
                <div
                  key={studentId}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-600 font-medium text-sm">
                      {index + 1}
                    </span>
                    <span className="text-gray-900">{getStudentName(studentId)}</span>
                  </div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => removeStudent(studentId)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg">
                No hay estudiantes asignados
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-3">
            <Link
              to="/admin/cohorts"
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={isSaving || !formData.level_id || !formData.teacher_id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Cohorte')}
            </button>
          </div>
        )}
      </form>

      {/* Student Selector Modal */}
      {showStudentSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Agregar Estudiante</h3>
              <button
                onClick={() => {
                  setShowStudentSelector(false)
                  setStudentSearch('')
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar estudiante..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                autoFocus
              />
            </div>

            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredStudents.length > 0 ? (
                filteredStudents.map(student => (
                  <button
                    key={student.id}
                    type="button"
                    onClick={() => addStudent(student.id)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {student.first_name} {student.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{student.email}</p>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No se encontraron estudiantes
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
